import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, MapPin, Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface JobPosting {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  questions?: string[];
  postingDate?: string | null;
  expiryDate?: string | null;
  uniqueId?: string;
}

const JobDetail = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  const [job, setJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (uniqueId) {
      fetchJob();
    }
  }, [uniqueId]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/jobs/by-id/${uniqueId}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Job posting not found or no longer available.');
        } else {
          throw new Error('Failed to fetch job details');
        }
        return;
      }
      const data = await res.json();
      setJob({
        ...data,
        requirements: data.requirements || [],
        questions: data.questions || [],
        postingDate: data.postingDate || null,
        expiryDate: data.expiryDate || null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load job details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
      </div>

      {/* Header with logo */}
      <header className="relative z-20 px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center rounded-xl border border-zinc-800 bg-[#0d0d0d]/90 backdrop-blur px-5 py-3">
            <Link to="/" className="flex items-center">
              <img src="/pattrnworks_logo_transparent_small.png" alt="PattrnWorks" className="h-9 brightness-0 invert" />
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/careers" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Careers</Link>
              <Link to="/#contact" className="hidden sm:flex bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 items-center">
                Start a project
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 px-4 md:px-8 py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Link
              to="/careers"
              className="inline-flex items-center text-zinc-400 hover:text-zinc-100 transition-colors"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              <span>Back to Careers</span>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12 text-zinc-400">
              Loading job details...
            </div>
          ) : error ? (
            <div className="bg-zinc-900/40 rounded-xl p-8 border border-zinc-800 shadow-2xl text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Link
                to="/careers"
                className="inline-flex items-center bg-violet-600 text-zinc-100 px-6 py-3 rounded-lg font-medium hover:bg-violet-500 transition-all duration-300"
              >
                <ArrowLeft className="mr-2 w-5 h-5" />
                Back to Careers
              </Link>
            </div>
          ) : job ? (
            <div className="bg-zinc-900/40 rounded-xl p-6 md:p-8 border border-zinc-800 shadow-2xl">
              {/* Job Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-zinc-100" />
                  </div>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-100 mb-2">
                      {job.title}
                    </h1>
                    <p className="text-zinc-400 text-lg">{job.department}</p>
                  </div>
                </div>

                {/* Job Meta Info */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center text-zinc-400 bg-zinc-800/60 px-4 py-2 rounded-lg">
                    <MapPin className="w-4 h-4 mr-2 text-violet-400" />
                    {job.location}
                  </div>
                  <div className="flex items-center text-zinc-400 bg-zinc-800/60 px-4 py-2 rounded-lg">
                    <Clock className="w-4 h-4 mr-2 text-violet-400" />
                    {job.type}
                  </div>
                  {job.salary && (
                    <div className="flex items-center text-zinc-400 bg-zinc-800/60 px-4 py-2 rounded-lg">
                      <span className="text-violet-400 mr-2 font-semibold">₨</span>
                      {job.salary}
                    </div>
                  )}
                </div>
              </div>

              {/* Job Description */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-zinc-100 mb-4">Job Description</h2>
                <p className="text-zinc-400 leading-relaxed whitespace-pre-line">
                  {job.description}
                </p>
              </div>

              {/* Requirements */}
              {job.requirements && job.requirements.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-zinc-100 mb-4">Requirements</h2>
                  <ul className="text-zinc-400 space-y-2">
                    {job.requirements.map((req, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-violet-400 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Apply Button */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <Link
                  to={job.uniqueId ? `/apply/${job.uniqueId}` : "/careers/apply"}
                  state={job.uniqueId ? undefined : { position: job.title, jobId: job.id, questions: job.questions || [] }}
                  className="inline-block w-full md:w-auto bg-violet-600 text-zinc-100 px-8 py-4 rounded-lg font-semibold hover:bg-violet-500 transition-all duration-300 transform hover:scale-105 shadow-lg text-center"
                >
                  Apply Now
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default JobDetail;

